/**
 * ตัวควบคุมเซสชันที่ทั้งสอง shell ใช้ร่วมกัน
 *
 * ปุ่มออกจากระบบมี implementation เดียวโดยเจตนา: D-046 ให้ handheld ถือ session
 * ยาว 16 ชั่วโมงและไม่มี idle timeout เพื่อให้ operator เข้าสู่ระบบครั้งเดียวต่อกะ
 * ราคาที่บันทึกไว้ตรงนั้นคือเครื่องที่ถูกวางทิ้งยังใช้งานต่อได้จนหมดอายุ ปุ่มนี้จึง
 * เป็นสิ่งที่ทำให้ข้อแลกเปลี่ยนนั้นยอมรับได้ ไม่ใช่ของประดับ — ถ้ามันแยกเป็นสองชุด
 * วันหนึ่งจะมี shell ที่ปุ่มพัง/หายไปโดยไม่มีใครรู้
 */

import type { CSSProperties } from 'react';

import { signOut, useSession } from './session';

export interface SignOutButtonProps {
  readonly className?: string;
  /** ใช้บังคับขนาด touch target บนเครื่องสแกน (`scanner/touch.ts`) */
  readonly style?: CSSProperties;
  readonly label?: string;
}

/**
 * ออกจากระบบ = ปิด session ที่เซิร์ฟเวอร์ (D-057) แล้วล้าง token กลับไปหน้าเข้าสู่ระบบ
 *
 * ทั้งสองอย่างอยู่ใน `signOut()` ที่เดียว ปุ่มนี้จึงไม่รู้จัก endpoint และไม่มีสถานะ
 * "กำลังออกจากระบบ" ให้รอ: `signOut()` ล้าง token ทันทีหลังยิงคำขอ หน้าจอจึงสลับไป
 * หน้า Login ทันทีที่กด ไม่ว่าเน็ตจะเป็นอย่างไร — ปุ่มออกจากระบบที่ค้างเพราะ API
 * ไม่ตอบ คือปุ่มที่พังในสถานการณ์ที่ต้องใช้มันที่สุด
 *
 * การกลับไปหน้า Login ไม่ได้ทำที่นี่แต่เกิดเองจาก store: `App` อ่านสถานะเซสชัน
 * ผ่าน `useSession` จึงเปลี่ยนหน้าจอให้ทุก shell พร้อมกันโดยไม่ต้องมี navigate
 * กระจายอยู่หลายที่
 */
export function SignOutButton({ className, style, label = 'ออกจากระบบ' }: SignOutButtonProps) {
  return (
    <button
      type="button"
      className={className}
      style={style}
      data-testid="sign-out"
      onClick={() => {
        // `void`: คำตอบของเซิร์ฟเวอร์เปลี่ยนผลลัพธ์บนเครื่องนี้ไม่ได้อยู่แล้ว
        void signOut();
      }}
    >
      {label}
    </button>
  );
}

export interface SessionPersistenceNoticeProps {
  readonly className?: string;
}

/**
 * บอกตามจริงเมื่อ token อยู่ในหน่วยความจำเท่านั้น
 *
 * เกิดในโหมดส่วนตัวหรือเบราว์เซอร์ที่ปิด storage ไว้ — แอปยังใช้งานได้ทั้งหมด แต่
 * เซสชันจะหายเมื่อรีเฟรช ซึ่งผู้ใช้ควรรู้ก่อนที่มันจะเกิดกลางงาน ไม่ใช่หลังจากนั้น
 */
export function SessionPersistenceNotice({ className }: SessionPersistenceNoticeProps) {
  const { token, persisted } = useSession();
  if (token === null || persisted) {
    return null;
  }
  return (
    <span className={className} data-testid="session-not-persisted" role="status">
      เบราว์เซอร์นี้เก็บเซสชันไม่ได้ — ต้องเข้าสู่ระบบใหม่เมื่อรีเฟรชหน้าจอ
    </span>
  );
}
